import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Splitter } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../LoadingPanel";
import { AdministratorLogin } from "./Login";
import { Checkbox, TextBox, InputClearValue } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Pager } from "@progress/kendo-react-data-tools";
import { Icon } from "@progress/kendo-react-common";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { ScreenSize } from '../../../app/ScreenSize';
import { GetScreenSize } from '../../../app/GetScreenSize';
import { getCSRFToken } from '../../../app/getCSRFToken';
import useSpeechToText from 'react-hook-speech-to-text';

import './Logins.scss';

export const AdministratorLogins = () => {
    const dispatch = useDispatch();

    const [logins, setLogins] = useState([]);
    const [loginsPagerData, setLoginsPagerData] = useState({
        total: 0,
        skip: 0,
        take: 1
    });
    const [selectedLoginKey, setSelectedLoginKey] = useState(null);
    const [selectedLogin, setSelectedLogin] = useState(undefined);
    const [areLoginsLoading, setAreLoginsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [doCreateNewLogin, setDoCreateNewLogin] = useState(false);
    const [loginNameFilterValue, setLoginNameFilterValue] = useState("");
    const [loginTypeIdFilterValue, setLoginTypeIdFilterValue] = useState(null);
    const [onlyActiveLoginsFilterValue, setOnlyActiveLoginsFilterValue] = useState(true);
    const [isLoginLoading, setIsLoginLoading] = useState(false);

    const defaultLoginType = {
        loginTypeId: "",
        name: "All Types",
    };
    const [loginTypes, setLoginTypes] = useState([]);
    const [loginTypesFilterOptions, setLoginTypesFilterOptions] = useState([defaultLoginType]);
    const [timeZones, setTimeZones] = useState([]);
    const [twoFactorAuthenticationTypes, setTwoFactorAuthenticationTypes] = useState([]);

    const typingTimeout = useRef();
    const typingTimeoutDelay = 500; //Milliseconds

    const {
        error,
        results,
        isRecording,
        startSpeechToText,
        stopSpeechToText,
    } = useSpeechToText({
        timeout: 1000
    });

    const standardPanes = [
        {
            size: "340px",
            min: "5%",
            resizable: false,
        },
        {}
    ];

    const getMobilePanes = () => {
        if (!selectedLogin && doCreateNewLogin === false) {
            return [
                {
                    size: "100%",
                    resizable: false,
                },
                {
                    collapsed: true,
                }
            ];
        }

        return [
            {
                size: "100%",
                resizable: false,
                collapsed: true,
            },
            {

            }
        ];
    }

    const renderLoginListItem = (props) => {
        let item = props.dataItem;

        return (
            <div className={"loginListItem" + (item.loginKey === selectedLoginKey ? " loginListItemSelected" : "")} onClick={() => selectLogin(item.loginKey)}>
                <div className="loginListPrimaryText">
                    {item.name}
                </div>
                <div className="loginListSecondaryText">
                    {item.isActive ? "Active" : "Inactive"}
                    &nbsp;|&nbsp;
                    {loginTypesFilterOptions.find(element => element.loginTypeId === item.loginTypeId)?.name}
                </div>
            </div>
        );
    }

    const selectLogin = (loginKey) => {
        getLogin(loginKey);
    }

    const createNewLogin = () => {
        setSelectedLogin(undefined);
        setSelectedLoginKey(null);
        setIsLoginLoading(false);
        setDoCreateNewLogin(true);

        getLogins(loginNameFilterValue, onlyActiveLoginsFilterValue, loginTypeIdFilterValue, loginsPagerData.skip, loginsPagerData.take);
    }

    const clearSelectedLogin = () => {
        setSelectedLogin(undefined);
        setSelectedLoginKey(null);
        setIsLoginLoading(false);
        setDoCreateNewLogin(false);

        getLogins(loginNameFilterValue, onlyActiveLoginsFilterValue, loginTypeIdFilterValue, loginsPagerData.skip, loginsPagerData.take);
    }

    const getLogin = (loginKey) => {
        setSelectedLoginKey(loginKey);
        setIsLoginLoading(true);
        setDoCreateNewLogin(false);

        const dataItem = {
            loginKey: loginKey,
        };

        fetch(`/api/administrator/getlogin`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                result.loginType = loginTypes.find(element => element.loginTypeId === result.loginTypeId);

                setSelectedLogin(result);
                setIsLoginLoading(false);

                getLogins(loginNameFilterValue, onlyActiveLoginsFilterValue, loginTypeIdFilterValue, loginsPagerData.skip, loginsPagerData.take);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setSelectedLogin(undefined);
                setIsLoginLoading(false);
            });
    }

    const getLogins = (name, onlyActive, loginType, skip, take) => {
        if (!skip) {
            skip = 0;
        }

        if (!take) {
            take = 1;
        }

        const pageNumber = (skip / take) + 1;

        const dataItem = {
            name: name,
            onlyActive: onlyActive,
            loginTypeId: loginType?.loginTypeId !== "" ? loginType?.loginTypeId : null,
            pageNumber: pageNumber,
        };

        fetch(`/api/administrator/logins`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreLoginsLoading(false);
                setLogins(result.logins);
                setLoginsPagerData({
                    total: result.totalLoginCount,
                    skip: (result.pageNumber - 1) * result.loginsPerPage,
                    take: result.loginsPerPage
                });
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreLoginsLoading(false);
                setLoadingError(error.message);
            });
    }

    const getLoginTypes = () => {
        fetch(`/api/administrator/logintypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allLoginTypes = [defaultLoginType].concat(result);
                setLoginTypes(result);
                setLoginTypesFilterOptions(allLoginTypes);
                setLoginTypeIdFilterValue(defaultLoginType);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getTimeZones = () => {
        fetch(`/api/general/timezones`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {                
                setTimeZones(result);       
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getTwoFactorAuthenticationTypes = () => {
        fetch(`/api/general/twofactorauthenticationtypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setTwoFactorAuthenticationTypes(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const handleLoginPageChange = (event) => {
        const { skip, take } = event;

        setLoginsPagerData({
            total: loginsPagerData.total,
            skip: skip,
            take: take
        });

        getLogins(loginNameFilterValue, onlyActiveLoginsFilterValue, loginTypeIdFilterValue, skip, take);
    };

    const loginNameFilterValueChangedEvent = (event) => {
        const value = event.value;
        loginNameFilterValueChanged(value);
    }

    const loginNameFilterValueChanged = (value) => {
        clearTimeout(typingTimeout.current);
        setLoginNameFilterValue(value);
        
        typingTimeout.current = setTimeout(() => {
            getLogins(value, onlyActiveLoginsFilterValue, loginTypeIdFilterValue);
        }, typingTimeoutDelay);
    }

    const loginNameClearFilterValue = () => {
        setLoginNameFilterValue("");
        getLogins("", onlyActiveLoginsFilterValue, loginTypeIdFilterValue);
    }

    const loginTypeFilterValueChanged = (event) => {
        const value = event.value;
        
        setLoginTypeIdFilterValue(value);
        getLogins(loginNameFilterValue, onlyActiveLoginsFilterValue, value);
    }

    const onlyActiveLoginsFilterValueChanged = (event) => {
        const value = event.value;

        setOnlyActiveLoginsFilterValue(value);
        getLogins(loginNameFilterValue, value, loginTypeIdFilterValue);
    }

    useEffect(() => {
        document.title = "Logins";

        getLoginTypes();
        getTimeZones();
        getTwoFactorAuthenticationTypes();
        getLogins(loginNameFilterValue, onlyActiveLoginsFilterValue, loginTypeIdFilterValue);
    }, []);

    useEffect(() => {
        if (!isRecording) {
            if (results.length > 0) {
                const speechValue = results.pop();
                loginNameFilterValueChanged(speechValue); 
            }
        }
    }, [isRecording]);

    return (
        <Splitter
            panes={GetScreenSize() === ScreenSize.Mobile ? getMobilePanes() : standardPanes}
        >
            <div>
                <div className="loginListFilterHeader">
                    <Button icon="plus" themeColor={"primary"} onClick={createNewLogin}>Create New Login</Button>
                </div>
                <div className="loginListFilter">
                    <div className="d-flex flex-row">
                        <TextBox
                            type={"text"}
                            maxLength={"50"}
                            value={loginNameFilterValue}
                            onChange={loginNameFilterValueChangedEvent}
                            placeholder={"Search part of event name"}   
                            suffix={() => (
                                <>
                                    {loginNameFilterValue !== "" && (
                                        <InputClearValue onClick={loginNameClearFilterValue}>
                                            <Icon name="x" />
                                        </InputClearValue>
                                    )}                                
                                </>
                            )}
                        />

                        {!error && <Button className={isRecording ? "recordButton recording pt-2 pb-0 ms-2" : "recordButton pt-2 pb-0 ms-2"} iconClass="fa-solid fa-microphone" onClick={isRecording ? stopSpeechToText : startSpeechToText} />}
                    </div>
                    <div>
                        <DropDownList
                            data={loginTypesFilterOptions}
                            value={loginTypeIdFilterValue}
                            textField="name"
                            dataItemKey="loginTypeId"
                            onChange={loginTypeFilterValueChanged}
                        />
                    </div>
                    <div>
                        <Checkbox
                            label={"Only active"}
                            onChange={onlyActiveLoginsFilterValueChanged}
                            value={onlyActiveLoginsFilterValue}
                        />
                    </div>
                </div>
                {areLoginsLoading ? <LoadingPanel /> : (loadingError === "" ? (
                    <>
                        <ListView
                            data={logins}
                            item={renderLoginListItem}
                        />
                        <Pager
                            skip={loginsPagerData.skip}
                            take={loginsPagerData.take}
                            total={loginsPagerData.total}
                            buttonCount={0}
                            info={true}
                            type={"numeric"}
                            previousNext={true}
                            onPageChange={handleLoginPageChange}
                        />
                    </>) : <div></div>)}
            </div>
            <AdministratorLogin
                login={selectedLogin}
                loading={isLoginLoading}
                createNewLogin={doCreateNewLogin}
                cancel={clearSelectedLogin}
                selectLogin={selectLogin}
                loginTypes={loginTypes}
                timeZones={timeZones}
                twoFactorAuthenticationTypes={twoFactorAuthenticationTypes}
            />
        </Splitter>
    );
}